import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { useOrgStore } from "@/store/org";
import clsx from "clsx";
import {
  Home, Map, Users, FileUp, Mail, Package, Truck, BarChart3, LayoutDashboard,
  DollarSign, LogOut, Settings, Milk, Menu, X, MapPin, UserCircle, Building2,
  AlertTriangle, Boxes, Bot, CreditCard,
} from "lucide-react";
import { useState } from "react";

interface NavItem {
  to: string;
  label: string;
  icon: any;
  end?: boolean;
}

const ADMIN_NAV: NavItem[] = [
  { to: "/admin", label: "대시보드", icon: Home, end: true },
  { to: "/admin/mission", label: "배송 관제", icon: LayoutDashboard },
  { to: "/admin/agent", label: "AI 비서", icon: Bot },
  { to: "/admin/import", label: "데이터 가져오기", icon: FileUp },
  { to: "/admin/students", label: "학생 관리", icon: Users },
  { to: "/admin/address-review", label: "주소 검토", icon: AlertTriangle },
  { to: "/admin/address-confirm", label: "주소 확인 대기", icon: MapPin },
  { to: "/admin/apartments", label: "아파트 라인", icon: Building2 },
  { to: "/admin/address-updates", label: "주소 변경 · 전출", icon: Mail },
  { to: "/admin/loading-lists", label: "상차 리스트", icon: Package },
  { to: "/admin/courier", label: "월말 택배", icon: Truck },
  { to: "/admin/inventory", label: "재고 관리", icon: Boxes },
  { to: "/admin/assign", label: "담당 동네 정하기", icon: MapPin },
  { to: "/admin/drivers", label: "기사 통계", icon: BarChart3 },
  { to: "/admin/pricing", label: "단가 시뮬", icon: DollarSign },
  { to: "/admin/users", label: "사용자 권한", icon: UserCircle },
  { to: "/admin/org", label: "환경 설정", icon: Settings },
  { to: "/admin/billing", label: "이용료", icon: CreditCard },
];

const DRIVER_NAV: NavItem[] = [
  { to: "/driver", label: "홈", icon: Home, end: true },
  { to: "/driver/claim", label: "이번달 담당", icon: MapPin },
  { to: "/driver/route", label: "오늘 경로", icon: Map },
  { to: "/driver/stats", label: "내 실적", icon: BarChart3 },
];

interface NavSection {
  title: string;
  items: NavItem[];
}

function getNavSections(role: string | null, orgId?: string): NavSection[] {
  if (role === "admin") {
    // 「단가 시뮬」은 부산 1호 업체의 우유 매입가를 계산하던 내부 도구라 다른 업체에는 숨긴다
    const adminNav = orgId === "freemilk" ? ADMIN_NAV : ADMIN_NAV.filter((n) => n.to !== "/admin/pricing");
    return [
      { title: "관리자", items: adminNav },
      { title: "기사 화면", items: DRIVER_NAV },
    ];
  }
  if (role === "driver") {
    return [{ title: "기사", items: DRIVER_NAV }];
  }
  return [];
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, role, orgId, signOut } = useAuth();
  const org = useOrgStore((s) => s.org);
  const loc = useLocation();
  const sections = getNavSections(role, orgId);
  const allItems = sections.flatMap((s) => s.items);
  const [mobileOpen, setMobileOpen] = useState(false);

  // 관리자/기사 모두 오늘 경로 빠른 진입을 위해 모바일 하단 탭 표시
  const showMobileTab = role === "driver" || role === "admin";
  const mobileTabItems = role === "admin"
    ? [
        { to: "/admin/mission", label: "관제", icon: LayoutDashboard, end: false },
        { to: "/driver/route", label: "경로", icon: Map, end: false },
        { to: "/driver/claim", label: "담당", icon: MapPin, end: false },
        { to: "/admin", label: "관리", icon: Home, end: true },
      ]
    : DRIVER_NAV;

  return (
    <div className="min-h-full flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-slate-200/70 sticky top-0 h-screen">
        <div className="px-5 py-5 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-brand grid place-items-center shadow-soft">
            <Milk className="w-5 h-5 text-white" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-bold text-slate-900 leading-tight">무상우유콕</div>
            <div className="text-xs text-slate-500">{org?.name || "배송 관리"}</div>
          </div>
        </div>

        <nav className="px-3 py-2 flex-1 overflow-y-auto">
          {sections.map((section, si) => (
            <div key={section.title} className={si > 0 ? "mt-4" : ""}>
              <div className="label px-3 py-2 flex items-center justify-between">
                <span>{section.title}</span>
                {role === "admin" && section.title === "기사 화면" && (
                  <span className="chip-brand text-2xs">현장 보기</span>
                )}
              </div>
              <div className="space-y-0.5">
                {section.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) =>
                      clsx("nav-item relative", isActive && "nav-item-active")
                    }
                  >
                    <n.icon className="w-4 h-4" strokeWidth={2} />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-slate-200/70 space-y-2">
          <div className="flex items-center gap-3 px-2">
            <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 grid place-items-center font-bold text-xs">
              {user?.email?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-900 truncate">{user?.displayName || user?.email}</div>
              <div className="text-2xs text-slate-500">
                {role === "admin" ? "관리자 + 기사" : role === "driver" ? "기사" : "권한대기"}
              </div>
            </div>
          </div>
          {!role && (
            <Link to="/" className="btn-secondary w-full text-xs">
              <Settings className="w-3.5 h-3.5" /> 처음 안내 보기
            </Link>
          )}
          <button onClick={signOut} className="btn-ghost w-full text-xs">
            <LogOut className="w-3.5 h-3.5" /> 로그아웃
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-72 bg-white h-full flex flex-col shadow-elevated-lg animate-fade-in">
            <div className="px-5 py-5 flex items-center gap-2 border-b border-slate-100">
              <div className="w-9 h-9 rounded-xl bg-gradient-brand grid place-items-center"><Milk className="w-5 h-5 text-white" strokeWidth={2.4} /></div>
              <div className="flex-1">
                <div className="font-bold">무상우유콕</div>
              </div>
              <button className="btn-ghost btn-sm" onClick={() => setMobileOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <nav className="p-3 flex-1 overflow-y-auto">
              {sections.map((section, si) => (
                <div key={section.title} className={si > 0 ? "mt-3 pt-3 border-t border-slate-100" : ""}>
                  <div className="label px-3 py-1.5">{section.title}</div>
                  <div className="space-y-0.5">
                    {section.items.map((n) => (
                      <NavLink
                        key={n.to}
                        to={n.to}
                        end={n.end}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) => clsx("nav-item", isActive && "nav-item-active")}
                      >
                        <n.icon className="w-4 h-4" /> {n.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
            </nav>
            <div className="p-3 border-t border-slate-100">
              {!role && <Link to="/" onClick={() => setMobileOpen(false)} className="btn-secondary w-full text-xs mb-2"><Settings className="w-3.5 h-3.5" /> 처음 안내 보기</Link>}
              <button onClick={signOut} className="btn-ghost w-full text-xs"><LogOut className="w-3.5 h-3.5" /> 로그아웃</button>
            </div>
          </aside>
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="topbar">
          <div className="h-14 px-4 lg:px-8 flex items-center gap-3 max-w-none">
            <button className="lg:hidden btn-ghost btn-sm" onClick={() => setMobileOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-2">
              <span className="lg:hidden font-bold text-slate-900">무상우유콕</span>
              <CurrentPageTitle path={loc.pathname} items={allItems} />
            </div>
            <div className="flex items-center gap-2">
              <span className={clsx(
                "chip",
                role === "admin" ? "chip-danger" :
                role === "driver" ? "chip-brand" : "chip-warning",
              )}>
                {role === "admin" ? "관리자 + 기사" : role === "driver" ? "기사" : "권한대기"}
              </span>
              <div className="hidden sm:flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-500 hidden md:inline">{user?.email}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-6 max-w-[1400px] w-full mx-auto" data-page>
          {children}
        </main>

        {/* Mobile bottom tab bar */}
        {showMobileTab && (
          <nav className="lg:hidden sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-slate-200">
            <div className={clsx(
              "grid",
              mobileTabItems.length === 4 ? "grid-cols-4" :
              mobileTabItems.length === 5 ? "grid-cols-5" :
              "grid-cols-3",
            )}>
              {mobileTabItems.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    clsx(
                      "flex flex-col items-center gap-0.5 py-2 text-2xs font-medium transition",
                      isActive ? "text-brand-600" : "text-slate-500",
                    )
                  }
                >
                  <n.icon className="w-5 h-5" strokeWidth={2} />
                  {n.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
}

function CurrentPageTitle({ path, items }: { path: string; items: NavItem[] }) {
  // 가장 구체적인 매칭(긴 to 우선)
  const match = items
    .filter((n) => (n.end ? path === n.to : path.startsWith(n.to)))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (!match) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <match.icon className="w-4 h-4 text-slate-400" />
      <span className="font-semibold text-slate-700">{match.label}</span>
    </div>
  );
}
